<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-chart-box-outline"></span></div>
      <div>
        <div class="id-h1">{{ $t('insightView.title') }}</div>
        <div class="pg-sub">{{ $t('insightView.subtitle') }}</div>
      </div>
      <div class="grid-spacer"></div>
      <button v-if="managerFlag" class="id-btn id-btn-primary" :disabled="generating" @click="generateNewInsight">
        <span class="mdi" :class="generating ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></span>
        {{ generating ? $t('insightView.generating') : $t('insightView.generateBtn') }}
      </button>
    </div>

    <!-- stat tiles: เทรนด์รายเดือน (12 เดือนย้อนหลัง ปิดรอบสมบูรณ์) -->
    <div v-if="loadingInsights" class="insight-stat-row">
      <div class="scard"><div class="id-skel" style="height:90px"></div></div>
      <div class="scard"><div class="id-skel" style="height:90px"></div></div>
    </div>
    <div v-else class="insight-stat-row id-fade-in" :key="'stats-' + (selectedInsight ? selectedInsight.insightid : 0)">
      <div class="scard">
        <div class="acc-clip"><div class="acc" style="background:linear-gradient(90deg,var(--c-primary),var(--c-primary-light))"></div></div>
        <div class="row" style="justify-content:space-between">
          <span class="lbl">{{ $t('insightView.newStudentsTile') }}</span>
          <span class="mdi mdi-account-plus" style="color:var(--c-primary);font-size:20px"></span>
        </div>
        <div class="val">{{ monthlyNewStudents.latestMonthCount }}</div>
        <div class="t-cap">
          <span v-if="monthlyNewStudents.momChangePct !== null" class="badge" :class="momBadgeClass(monthlyNewStudents.momChangePct)">
            <span class="mdi" :class="momIcon(monthlyNewStudents.momChangePct)"></span>
            {{ Math.abs(monthlyNewStudents.momChangePct) }}% {{ $t('insightView.vsPrevMonth') }}
          </span>
        </div>
        <div class="spark-wrap">
          <svg width="120" height="32" viewBox="0 0 120 32" style="display:block">
            <polyline :points="polylineStr(cohortPoints)" fill="none" stroke="var(--c-text-muted)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <circle v-for="(p, i) in cohortPoints" :key="i" :cx="p.x" :cy="p.y" r="10" fill="transparent" tabindex="0"
              @mouseenter="showSparkTip('cohort', p)" @mouseleave="hideSparkTip('cohort')"
              @focus="showSparkTip('cohort', p)" @blur="hideSparkTip('cohort')"></circle>
            <circle v-if="cohortPoints.length" class="spark-dot-last" :cx="cohortPoints[cohortPoints.length - 1].x" :cy="cohortPoints[cohortPoints.length - 1].y" r="4" fill="var(--c-primary)" />
          </svg>
          <div v-if="sparkTip.visible && sparkTip.which === 'cohort'" class="spark-tip" :style="{ left: sparkTip.x + 'px', top: sparkTip.y + 'px' }">{{ sparkTip.text }}</div>
        </div>
        <button type="button" class="spark-toggle" :aria-expanded="showCohortTable" @click="showCohortTable = !showCohortTable">
          <span class="mdi" :class="showCohortTable ? 'mdi-chevron-up' : 'mdi-chevron-down'"></span>
          {{ showCohortTable ? $t('insightView.hideMonthly') : $t('insightView.showMonthly') }}
        </button>
        <table v-if="showCohortTable" class="spark-table-full">
          <tbody>
            <tr v-for="m in monthlyNewStudents.months" :key="m.ym">
              <td>{{ monthLabel(m.ym) }}</td>
              <td>{{ m.count }} {{ $t('insightView.unitPeople') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="scard">
        <div class="acc-clip"><div class="acc" style="background:linear-gradient(90deg,var(--c-secondary),var(--c-secondary-light))"></div></div>
        <div class="row" style="justify-content:space-between">
          <span class="lbl">{{ $t('insightView.packagesSoldTile') }}</span>
          <span class="mdi mdi-package-variant-closed" style="color:var(--c-secondary);font-size:20px"></span>
        </div>
        <div class="val">{{ monthlyPackagesSold.latestMonthCount }}</div>
        <div class="t-cap">
          <span v-if="monthlyPackagesSold.momChangePct !== null" class="badge" :class="momBadgeClass(monthlyPackagesSold.momChangePct)">
            <span class="mdi" :class="momIcon(monthlyPackagesSold.momChangePct)"></span>
            {{ Math.abs(monthlyPackagesSold.momChangePct) }}% {{ $t('insightView.vsPrevMonth') }}
          </span>
        </div>
        <div class="spark-wrap">
          <svg width="120" height="32" viewBox="0 0 120 32" style="display:block">
            <polyline :points="polylineStr(packagesPoints)" fill="none" stroke="var(--c-text-muted)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <circle v-for="(p, i) in packagesPoints" :key="i" :cx="p.x" :cy="p.y" r="10" fill="transparent" tabindex="0"
              @mouseenter="showSparkTip('packages', p)" @mouseleave="hideSparkTip('packages')"
              @focus="showSparkTip('packages', p)" @blur="hideSparkTip('packages')"></circle>
            <circle v-if="packagesPoints.length" class="spark-dot-last" :cx="packagesPoints[packagesPoints.length - 1].x" :cy="packagesPoints[packagesPoints.length - 1].y" r="4" fill="var(--c-secondary)" />
          </svg>
          <div v-if="sparkTip.visible && sparkTip.which === 'packages'" class="spark-tip" :style="{ left: sparkTip.x + 'px', top: sparkTip.y + 'px' }">{{ sparkTip.text }}</div>
        </div>
        <button type="button" class="spark-toggle" :aria-expanded="showPackagesTable" @click="showPackagesTable = !showPackagesTable">
          <span class="mdi" :class="showPackagesTable ? 'mdi-chevron-up' : 'mdi-chevron-down'"></span>
          {{ showPackagesTable ? $t('insightView.hideMonthly') : $t('insightView.showMonthly') }}
        </button>
        <table v-if="showPackagesTable" class="spark-table-full">
          <tbody>
            <tr v-for="m in monthlyPackagesSold.months" :key="m.ym">
              <td>{{ monthLabel(m.ym) }}</td>
              <td>{{ m.count }} {{ $t('insightView.unitPackages') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- รายงานล่าสุด + ประวัติ -->
    <div v-if="loadingInsights" class="scard" style="margin-top:14px">
      <div class="id-skel" style="height:120px"></div>
    </div>
    <div v-else-if="!insights.length" class="id-empty">
      <span class="mdi mdi-file-chart-outline"></span>
      <div>{{ $t('insightView.emptyReports') }}</div>
    </div>
    <template v-else>
      <div class="insight-body id-fade-in" :key="selectedInsight.insightid">
        <div class="scard" style="margin-top:14px">
          <div class="row" style="justify-content:space-between;align-items:flex-start">
            <span class="lbl" style="font-size:16px;color:var(--c-text-heading)">{{ selectedInsight.headline }}</span>
            <span class="t-cap">{{ formatDateTimeBKK(selectedInsight.createdate) }}</span>
          </div>
          <div class="insight-md" v-html="formatMarkdown(selectedInsight.body_markdown || selectedInsight.message)"></div>
        </div>

        <div v-if="insights.length > 1" class="scard" style="margin-top:14px">
          <div class="lbl" style="margin-bottom:8px">{{ $t('insightView.historyTitle') }}</div>
          <div v-for="it in insights" :key="it.insightid" class="insight-history-row" :class="{ on: it.insightid === selectedInsight.insightid }" @click="selectInsight(it)">
            <span class="mdi mdi-file-document-outline"></span>
            <span style="flex:1">{{ it.headline }}</span>
            <span class="t-cap">{{ formatDateTimeBKK(it.createdate) }}</span>
          </div>
        </div>

        <!-- ลิงก์ไปหน้าตรวจสอบประจำวัน — ตารางรายการปฏิบัติการ (ค้างชำระ/ใกล้หมด/ไม่เริ่มใช้/
             หมดอายุแต่ยังเหลือครั้ง) ถูกย้ายไปที่นั่นทั้งหมดเมื่อ 2026-08-05
             เหตุผล: หน้านี้เป็น "ภาพนิ่ง" ที่แช่แข็งไว้ตอนสร้างรายงาน (ดูวันที่บนหัวรายงาน) ลูกค้าที่
             ต่ออายุวันถัดมาจะหายจากหน้าตรวจสอบทันทีแต่ยังค้างอยู่ในนี้จนกว่าจะสร้างรายงานรอบใหม่ —
             คนที่เปิดสองหน้าเทียบกันจะคิดว่าระบบพัง · ที่เหลือไว้ในนี้คือของที่ ops ไม่มี (ชวนกลับ /
             เสี่ยงใช้ไม่ทัน) และตัวเลขสรุป/บทวิเคราะห์ซึ่งเป็นงานคนละจังหวะ -->
        <div class="scard insight-ops-link" style="margin-top:14px">
          <span class="mdi mdi-clipboard-alert-outline"></span>
          <div style="flex:1">
            <div class="lbl">{{ $t('insightView.opsLinkTitle') }}</div>
            <div class="t-cap">{{ $t('insightView.opsLinkBody') }}</div>
          </div>
          <button class="id-btn id-btn-ghost id-btn-sm" @click="$emit('onClickChangeState', 'opschecklist')">
            <span class="mdi mdi-open-in-new"></span> {{ $t('insightView.opsLinkBtn') }}
          </button>
        </div>

        <!-- Win-back — ไม่มีในหน้าตรวจสอบประจำวัน (ต้องใช้ประวัติแพ็กเกจที่จบไปแล้ว) -->
        <div class="insight-section-head">
          <span class="mdi mdi-account-heart-outline"></span> {{ $t('insightView.winBackTitle') }}
          <span class="badge badge-neutral">{{ winBackTotal }}</span>
        </div>
        <id-data-grid :columns="winBackColumns" :rows="winBackItems" :search-keys="['nickname', 'mobileno']" :per-page="10">
          <template #cell-endedOn="{ row }">{{ formatDateBKK(row.endedOn) }}</template>
          <template #cell-mobileno="{ row }">
            <a v-if="row.mobileno" :href="'tel:' + row.mobileno">{{ row.mobileno }}</a>
            <span v-else>-</span>
          </template>
        </id-data-grid>

        <!-- เสี่ยงใช้ไม่ทันก่อนหมดอายุ — ไม่มีในหน้าตรวจสอบประจำวันเช่นกัน (คิดจากจังหวะการใช้) -->
        <div class="insight-section-head">
          <span class="mdi mdi-speedometer" ></span> {{ $t('insightView.atRiskTitle') }}
          <span class="badge badge-neutral">{{ atRiskTotal }}</span>
        </div>
        <id-data-grid :columns="atRiskColumns" :rows="atRiskItems" :search-keys="['coursename', 'courserefer', 'students', 'phones']" :per-page="10">
          <template #cell-expiredate="{ row }">{{ formatDateBKK(row.expiredate) }}</template>
          <template #cell-remaining="{ row }">{{ row.remaining ?? '-' }}</template>
        </id-data-grid>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { formatDateBKK, formatDateTimeBKK } from '@/utils/datetime';
import { formatDateIntl } from '@/utils/locale';

export default {
  name: 'InsightView',
  props: {
    // ปุ่ม "สร้างรายงานใหม่" เฉพาะผู้จัดการ (usertype '0') — App.vue ส่ง managerflag ของมันเองลงมา
    // (ตัว flag จริงมีแค่ใน App.vue เท่านั้น ไม่มีที่เก็บกลาง จึงส่งผ่าน prop แบบเดียวกับ :ui-theme/:student)
    managerFlag: { type: Boolean, default: false },
  },
  data() {
    return {
      loadingInsights: false,
      generating: false,
      insights: [],
      selectedId: null,
      sparkTip: { visible: false, which: '', x: 0, y: 0, text: '' },
      showCohortTable: false,
      showPackagesTable: false,
    };
  },
  computed: {
    // column configs เป็น computed (ไม่ใช่ data()) เพื่อให้ label รีแอคทีฟตามภาษาที่เลือก
    // ($t อ่าน i18nState.locale ซึ่งเป็น reactive ref — ต้องอยู่ใน tracked context ถึงจะอัปเดตเองตอนสลับภาษา)
    winBackColumns() {
      return [
        { key: 'nickname', label: this.$t('insightView.colNickname') },
        { key: 'mobileno', label: this.$t('insightView.colMobile') },
        { key: 'endedOn', label: this.$t('insightView.colEndedOn'), align: 'center' },
        { key: 'daysLapsed', label: this.$t('insightView.colDaysLapsed'), align: 'center' },
      ];
    },
    atRiskColumns() {
      return [
        { key: 'coursename', label: this.$t('insightView.colCourse') },
        { key: 'courserefer', label: this.$t('insightView.colRef') },
        { key: 'students', label: this.$t('insightView.colStudents') },
        { key: 'phones', label: this.$t('insightView.colPhones') },
        { key: 'remaining', label: this.$t('insightView.colRemaining'), align: 'center' },
        { key: 'daysLeft', label: this.$t('insightView.colDaysLeft'), align: 'center' },
        { key: 'expiredate', label: this.$t('insightView.colExpiredate'), align: 'center' },
      ];
    },
    selectedInsight() {
      if (!this.insights.length) return null;
      return this.insights.find((i) => i.insightid === this.selectedId) || this.insights[0];
    },
    metrics() {
      const raw = this.selectedInsight && this.selectedInsight.metrics_json;
      if (!raw) return {};
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    },
    monthlyNewStudents() {
      return this.metrics.monthlyNewStudents || { months: [], latestMonthCount: 0, momChangePct: null };
    },
    monthlyPackagesSold() {
      return this.metrics.monthlyPackagesSold || { months: [], latestMonthCount: 0, momChangePct: null };
    },
    cohortPoints() { return this.computeSparkPoints(this.monthlyNewStudents.months); },
    packagesPoints() { return this.computeSparkPoints(this.monthlyPackagesSold.months); },

    // ⚠️ backend (insight.js) ยังเก็บ metric ทุกตัวไว้ครบเหมือนเดิม — LLM ใช้เป็นวัตถุดิบเขียน
    // บทวิเคราะห์ และ metrics_json เป็นประวัติย้อนหลังที่เทียบกันได้ · ที่ตัดออกคือ "การ render
    // เป็นตาราง" เท่านั้น (ตารางพวกนั้นย้ายไปหน้าตรวจสอบประจำวันซึ่งเป็นข้อมูลสด)
    winBackItems() { return (this.metrics.winBackList && this.metrics.winBackList.items) || []; },
    winBackTotal() { return (this.metrics.winBackList && this.metrics.winBackList.total) ?? this.winBackItems.length; },
    atRiskItems() { return (this.metrics.atRiskPace && this.metrics.atRiskPace.items) || []; },
    atRiskTotal() { return (this.metrics.atRiskPace && this.metrics.atRiskPace.total) ?? this.atRiskItems.length; },
  },
  async created() {
    try {
      const token = this.$store.getters.getToken;
      if (!token) {
        this.$emit('onClickChangeState', 'login');
        return;
      }
      await axios
        .post(this.baseURL + '/verifyToken', {}, { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => {
          if (response.data.success) this.initialize();
        })
        .catch((error) => {
          this.$emit('onErrorHandler', error.response ? error.response.data.message : error.message);
          this.$emit('onClickChangeState', 'login');
        });
    } catch (error) {
      this.$emit('onErrorHandler', error.message);
    }
  },
  methods: {
    formatDateBKK,
    formatDateTimeBKK,
    async initialize() {
      this.$emit('onLoading', true);
      await this.fetchInsights();
      this.$emit('onLoading', false);
    },
    async fetchInsights() {
      this.loadingInsights = true;
      const token = this.$store.getters.getToken;
      try {
        const response = await axios.post(this.baseURL + '/getInsights', {}, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) {
          this.insights = response.data.insights || [];
          if (this.insights.length) this.selectedId = this.insights[0].insightid;
        }
      } catch (error) {
        this.$emit('onErrorHandler', error.response ? error.response.data.message : error.message);
      }
      this.loadingInsights = false;
    },
    selectInsight(item) {
      this.selectedId = item.insightid;
    },
    async generateNewInsight() {
      this.generating = true;
      const token = this.$store.getters.getToken;
      try {
        const response = await axios.post(this.baseURL + '/generateInsight', {}, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success && response.data.insight) {
          this.insights.unshift(response.data.insight);
          this.selectedId = response.data.insight.insightid;
          this.$emit('onSuccessHandler', this.$t('insightView.generateSuccess'));
        } else {
          this.$emit('onErrorHandler', response.data.message || this.$t('insightView.generateFail'));
        }
      } catch (error) {
        this.$emit('onErrorHandler', error.response ? error.response.data.message : error.message);
      }
      this.generating = false;
    },
    momBadgeClass(pct) { return pct >= 0 ? 'badge-success' : 'badge-error'; },
    momIcon(pct) { return pct >= 0 ? 'mdi-arrow-up-bold' : 'mdi-arrow-down-bold'; },
    // hover/focus tooltip ต่อจุดข้อมูล sparkline — แทน <title> เดิม (ช้า/ไม่มีสไตล์) ด้วย tooltip ที่ควบคุมเอง
    // ข้อมูลทั้งหมดยังเปิดดูได้แบบไม่ต้อง hover ผ่านตาราง (ปุ่ม "ดูข้อมูลรายเดือน") อยู่แล้ว
    showSparkTip(which, p) {
      const unit = which === 'cohort' ? this.$t('insightView.unitPeople') : this.$t('insightView.unitPackages');
      this.sparkTip = { visible: true, which, x: p.x, y: p.y, text: `${this.monthLabel(p.ym)}: ${p.count} ${unit}` };
    },
    hideSparkTip(which) {
      if (this.sparkTip.which === which) this.sparkTip.visible = false;
    },
    monthLabel(ym) {
      // short month + 2-digit year in the selected language (Buddhist era for Thai)
      return formatDateIntl(new Date(ym + '-01T00:00:00+07:00'), { month: 'short', year: '2-digit' });
    },
    computeSparkPoints(months) {
      const w = 120, h = 32, pad = 4;
      const n = months.length;
      if (!n) return [];
      const max = Math.max(1, ...months.map((m) => m.count));
      return months.map((m, i) => ({
        x: n > 1 ? pad + (i * (w - 2 * pad)) / (n - 1) : w / 2,
        y: h - pad - (m.count / max) * (h - 2 * pad),
        ym: m.ym,
        count: m.count,
      }));
    },
    polylineStr(points) {
      return points.map((p) => `${p.x},${p.y}`).join(' ');
    },
    // แปลง body_markdown/message (รูปแบบง่าย: **bold**, บรรทัดขึ้นต้น •/-, ## หัวข้อ) เป็น HTML อย่างปลอดภัย
    // escape ก่อนเสมอ (กัน XSS) แล้วค่อยแปลงเฉพาะ pattern ที่รู้จัก — ไม่ใช้ lib markdown เต็มรูปแบบ
    formatMarkdown(text) {
      if (!text) return '';
      const esc = String(text)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      let html = esc.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      html = html.replace(/^###\s+(.+)$/gm, '<h5>$1</h5>').replace(/^##\s+(.+)$/gm, '<h4>$1</h4>');
      const lines = html.split('\n');
      const out = [];
      let inList = false;
      for (const line of lines) {
        const bullet = /^\s*[•\-*]\s+(.*)$/.exec(line);
        if (bullet) {
          if (!inList) { out.push('<ul>'); inList = true; }
          out.push(`<li>${bullet[1]}</li>`);
        } else {
          if (inList) { out.push('</ul>'); inList = false; }
          if (line.trim() === '') out.push('<br>'); else out.push(`<p>${line}</p>`);
        }
      }
      if (inList) out.push('</ul>');
      return out.join('');
    },
  },
};
</script>

<style scoped>
.insight-stat-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px; }
/* .spark-tip is absolutely positioned inside the card and is wider than the 120px
   sparkline, so the shared `.scard{overflow:hidden}` sliced it at the first/last month.
   Opt these cards out. The accent bar was relying on that same clip, so it now sits in
   its own clipping layer that follows the card's rounded corners. (OpsChecklist solves
   the tooltip clip with .ops-trend.) */
.insight-stat-row .scard { overflow: visible; }
.insight-stat-row .acc-clip { position: absolute; inset: 0; border-radius: inherit; overflow: hidden; pointer-events: none; }
.spark-wrap { position: relative; margin-top: 8px; width: 120px; }
/* the "current month" dot is decoration drawn AFTER the transparent r=10 hover
   targets, so without this it swallows the pointer and the LAST point is the one
   month whose tooltip never appears — you had to aim ~7px to its left to get it.
   Same rule OpsChecklist puts on .ops-spark-dot. */
.spark-dot-last { pointer-events: none; }
/* same skin as the app-wide hover hint (.sys-tip / .nav-tip in istar-pages.css) */
.spark-tip {
  position: absolute; transform: translate(-50%, -125%); pointer-events: none; z-index: 5;
  background: #22132f; color: #fff; font-size: 12.5px; font-weight: 600; white-space: nowrap;
  padding: 7px 11px; border-radius: 9px; box-shadow: var(--shadow-md);
}
.spark-tip::before {
  content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
  border: 6px solid transparent; border-top-color: #22132f;
}
.spark-toggle {
  display: inline-flex; align-items: center; gap: 2px; margin-top: 6px; padding: 0;
  background: none; border: none; font-size: 11px; color: var(--c-text-muted); font-family: var(--font-body); cursor: pointer;
}
.spark-toggle:hover { color: var(--c-primary); }
.spark-table-full { width: 100%; margin-top: 4px; border-collapse: collapse; font-size: 12px; }
.spark-table-full td { padding: 3px 4px; border-bottom: 1px solid var(--c-border); color: var(--c-text-body); }
.spark-table-full td:first-child { color: var(--c-text-muted); }
.spark-table-full td:last-child { text-align: right; font-variant-numeric: tabular-nums; }
.insight-section-head { display: flex; align-items: center; gap: 8px; margin: 20px 0 8px; font-family: var(--font-head); font-weight: 700; color: var(--c-text-heading); }
.insight-ops-link { display: flex; align-items: center; gap: 12px; }
.insight-ops-link > .mdi { font-size: 22px; color: var(--c-primary); flex: none; }
/* จอแคบ: ปุ่มตกลงมาบรรทัดล่างแทนที่จะบีบข้อความจนอ่านไม่ออก */
@media (max-width: 520px) {
  .insight-ops-link { flex-wrap: wrap; }
  .insight-ops-link > button { width: 100%; justify-content: center; }
}
.insight-history-row { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: var(--radius-sm); cursor: pointer; font-size: 13px; }
.insight-history-row:hover { background: var(--c-surface-3); }
.insight-history-row.on { background: var(--c-primary-soft); color: var(--c-primary-dark); }
.insight-md :deep(h4) { font-size: 15px; margin: 12px 0 6px; color: var(--c-text-heading); }
.insight-md :deep(h5) { font-size: 13px; margin: 10px 0 4px; color: var(--c-text-heading); }
.insight-md :deep(p) { margin: 4px 0; font-size: 14px; color: var(--c-text-body); line-height: 1.6; }
.insight-md :deep(ul) { margin: 4px 0 8px 20px; padding: 0; }
.insight-md :deep(li) { font-size: 14px; color: var(--c-text-body); line-height: 1.6; }
</style>
